<template>
  <div
    role="button"
    tabindex="0"
    class="w-full p-3 hover:bg-blue-50 cursor-pointer transition-colors text-left"
    :class="{ 'bg-blue-50': isActive }"
    @click="handleClick"
    @keydown.enter.prevent="handleClick"
    @keydown.space.prevent="handleClick"
  >
    <div class="flex items-center space-x-3">
      <div
        class="relative h-12 w-12 rounded-full flex items-center justify-center overflow-hidden shrink-0"
        :class="isActive ? 'bg-blue-200 text-blue-800' : 'bg-gray-100 text-gray-600'"
      >
        <img
          v-if="canShowAvatar"
          :src="avatarUrl"
          :alt="conversationName"
          class="h-12 w-12 object-cover"
          @error="avatarLoadFailed = true"
        />
        <span v-else-if="isDepartment" class="material-symbols-outlined text-2xl">account_balance</span>
        <span v-else-if="isGroup" class="material-symbols-outlined text-2xl">groups</span>
        <span v-else class="font-medium">{{ initials }}</span>
      </div>

      <div class="flex-1 min-w-0">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <div class="flex items-center gap-1.5 min-w-0">
              <h3 class="text-sm font-semibold text-gray-900 truncate">{{ conversationName }}</h3>
              <img
                v-if="isDepartment"
                :src="verifiedBadge"
                alt="Verified"
                class="h-4 w-4 shrink-0"
              />
            </div>
            <p v-if="isGroup" class="text-xs text-gray-500 truncate">
              {{ memberCount }} members
            </p>
            <p v-else-if="isDepartment" class="text-xs text-blue-700 truncate">
              Verified department
            </p>
          </div>
          <span class="text-xs text-gray-500 shrink-0">{{ formattedTime }}</span>
        </div>
        <p class="text-xs text-gray-500 truncate mt-0.5">{{ latestMessage || 'No messages yet' }}</p>
      </div>

      <div class="flex items-center gap-2 shrink-0">
        <div v-if="hasUnread" class="w-3 h-3 rounded-full bg-blue-600"></div>
        <button
          v-if="canDelete"
          type="button"
          class="h-8 w-8 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors flex items-center justify-center"
          aria-label="Delete conversation"
          @click.stop="$emit('delete', conversation)"
        >
          <span class="material-symbols-outlined text-lg">delete</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import {
  getConversationType,
  getConversationTitle,
  getConversationAvatar
} from '@/composables/useChat';
import verifiedBadge from '@/assets/verifiedBadge.png';

export default {
  data() {
    return {
      avatarLoadFailed: false,
      verifiedBadge
    };
  },
  props: {
    conversation: {
      type: Object,
      required: true
    },
    isActive: {
      type: Boolean,
      default: false
    },
    otherUser: {
      type: Object,
      default: null
    },
    currentUserId: {
      type: String,
      default: ''
    },
    canDelete: {
      type: Boolean,
      default: false
    }
  },
  emits: ['click', 'delete'],
  computed: {
    ...mapState(['user']),
    viewerId() {
      return this.currentUserId || this.user?.uid || '';
    },
    isGroup() {
      return getConversationType(this.conversation) === 'group';
    },
    isDepartment() {
      return getConversationType(this.conversation) === 'department';
    },
    memberCount() {
      return this.conversation.participants?.length || 0;
    },
    conversationName() {
      if (this.otherUser && !this.isGroup) {
        return this.otherUser.displayName || this.otherUser.name || 'Student';
      }

      return getConversationTitle(this.conversation, this.viewerId);
    },
    avatarUrl() {
      if (this.otherUser && !this.isGroup) {
        return this.otherUser.profilePicture || '';
      }

      return getConversationAvatar(this.conversation, this.viewerId);
    },
    canShowAvatar() {
      return Boolean(this.avatarUrl) && !this.avatarLoadFailed;
    },
    initials() {
      return this.conversationName
        .split(' ')
        .filter(Boolean)
        .map(part => part[0])
        .join('')
        .substring(0, 2)
        .toUpperCase() || '?';
    },
    latestMessage() {
      const lastMessage = this.conversation.lastMessage;
      if (!lastMessage) return '';

      const text = lastMessage.text || '';
      if (this.isDepartment && lastMessage.senderType === 'department') {
        return text;
      }

      if (this.isDepartment && lastMessage.senderId && lastMessage.senderId !== this.viewerId) {
        return `${lastMessage.senderName || 'Student'}: ${text}`;
      }

      if (this.isGroup && lastMessage.senderId && lastMessage.senderId !== this.viewerId) {
        return `${lastMessage.senderName || 'Student'}: ${text}`;
      }

      return text;
    },
    formattedTime() {
      const timestamp = this.conversation.lastMessage?.timestamp || this.conversation.updatedAt;
      if (!timestamp) return '';

      const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
      const today = new Date();
      const isToday =
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear();

      if (isToday) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }

      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const isYesterday =
        date.getDate() === yesterday.getDate() &&
        date.getMonth() === yesterday.getMonth() &&
        date.getFullYear() === yesterday.getFullYear();

      if (isYesterday) {
        return 'Yesterday';
      }

      return date.toLocaleDateString();
    },
    hasUnread() {
      return this.conversation.hasUnread && !this.isActive;
    }
  },
  methods: {
    ...mapActions(['setSelectedConversation']),
    handleClick() {
      this.setSelectedConversation(this.conversation);
      this.$emit('click', this.conversation);
    }
  },
  watch: {
    avatarUrl() {
      this.avatarLoadFailed = false;
    }
  }
};
</script>
